import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

const TOKEN_KEY = 'auth_token';

// 请求拦截：自动附加 JWT token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截：401 → 清除 token
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 不清除 /auth/login 的 401（那是密码错误）
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/forgot-password')) {
        localStorage.removeItem(TOKEN_KEY);
        // 触发自定义事件通知 AuthContext
        window.dispatchEvent(new Event('auth:logout'));
      }
    }
    return Promise.reject(error);
  }
);

export interface Student {
  id: number;
  name: string;
  school: string;
  grade: string;
  subject_type: string;
  grade_total: number;
  target_level: string;
  education_level?: 'senior' | 'junior';
  is_default: number;
  notes: string;
  graduation_year?: number;
  created_at: string;
  latest_score?: { total_score: number; grade_rank: number; exam_name: string; exam_date: string };
  score_count?: number;
}

export interface Score {
  id: number;
  student_id: number;
  exam_name: string;
  exam_date: string;
  exam_type: string;
  total_score: number;
  chinese_score?: number;
  math_score?: number;
  english_score?: number;
  physics_score?: number;
  chemistry_score?: number;
  geography_score?: number;
  history_score?: number;
  politics_score?: number;
  biology_score?: number;
  sports_score?: number;
  raw_physics_score?: number;
  raw_chemistry_score?: number;
  raw_geography_score?: number;
  raw_history_score?: number;
  raw_politics_score?: number;
  raw_biology_score?: number;
  class_rank?: number;
  grade_rank?: number;
  province_rank_estimate?: number;
  notes?: string;
}

export interface School {
  id: number;
  school_id: string;
  name: string;
  province: string;
  city: string;
  is985: number;
  is211: number;
  is_dual_class: number;
  school_type: string;
  website?: string;
  logo_url?: string;
  has_academician?: number;
  academician_count?: number;
  latest_admission?: { year: number; lowest_score: number; lowest_rank: number };
}

export interface SchoolAdmission {
  id: number;
  school_name: string;
  year: number;
  lowest_score: number;
  lowest_rank: number;
  avg_score: number;
  batch_name: string;
  course_selection_name: string;
}

export interface MajorAdmission {
  id: number;
  school_name: string;
  major_name: string;
  year: number;
  lowest_score: number;
  lowest_rank: number;
  avg_score: number;
  subject_selection: string;
  is985?: number;
  is211?: number;
  city?: string;
}

export interface RecommendSchool {
  school_name: string;
  is985: number;
  is211: number;
  city: string;
  school_province: string;
  avg_rank: number;
  avg_score: number;
  variance: number;
  year_count: number;
  years: Record<number, { lowest_rank: number; lowest_score: number }>;
  preference_score?: number;
  match_reasons?: string[];
}

export interface RecommendResult {
  province_rank?: number;
  school_count: number;
  chong: (RecommendSchool | HighSchool)[];
  wen: (RecommendSchool | HighSchool)[];
  bao: (RecommendSchool | HighSchool)[];
  score?: number;
  year?: number;
  education_level?: 'senior' | 'junior';
  student_score?: number;
  method?: string;
  message?: string;
  // 中考定向生支持
  zhao_sheng?: { chong: HighSchool[]; wen: HighSchool[]; bao: HighSchool[] };
  ding_xiang?: { chong: HighSchool[]; wen: HighSchool[]; bao: HighSchool[] };
  zhao_sheng_count?: number;
  ding_xiang_count?: number;
  preferences_used?: {
    target_cities: string[];
    target_majors: string[];
    school_type_preference: string;
    exclude_cities: string[];
  };
}

// 中考高中推荐数据
export interface HighSchool {
  name: string;
  district: string;
  school_type: string;
  is_demonstration: number;
  score_line: number;
  rank_line?: number;
  quota_type?: string;
  year?: number;
}

// 学生管理
export const studentsApi = {
  list: (params?: { name?: string }) => api.get('/students', { params }),
  get: (id: number) => api.get(`/students/${id}`),
  create: (data: Partial<Student>) => api.post('/students', data),
  update: (id: number, data: Partial<Student>) => api.put(`/students/${id}`, data),
  delete: (id: number) => api.delete(`/students/${id}`),
};

// 成绩管理
export const scoresApi = {
  list: (params: { student_id: number; latest?: number; exam_type?: string; year?: string }) =>
    api.get('/scores', { params }),
  get: (id: number) => api.get(`/scores/${id}`),
  create: (data: Partial<Score>) => api.post('/scores', data),
  createBatch: (student_id: number, records: Partial<Score>[]) =>
    api.post('/scores/batch', { student_id, records }),
  update: (id: number, data: Partial<Score>) => api.put(`/scores/${id}`, data),
  delete: (id: number) => api.delete(`/scores/${id}`),
  trend: (student_id: number) => api.get('/scores/stats/trend', { params: { student_id } }),
  subjects: (student_id: number) => api.get('/scores/subjects', { params: { student_id } }),
};

// 院校管理
export const schoolsApi = {
  list: (params?: Record<string, string | number>) => api.get('/schools', { params }),
  detail: (name: string) => api.get(`/schools/${encodeURIComponent(name)}/detail`),
  admission: (name: string, courseSelection?: string, province?: string) =>
    api.get(`/schools/${encodeURIComponent(name)}/admission`, { 
      params: { 
        province: province || '福建',
        course_selection: courseSelection
      } 
    }),
  provinces: () => api.get('/schools/meta/provinces'),
  cities: (province?: string) => {
    if (province) {
      return api.get(`/schools/meta/cities/${encodeURIComponent(province)}`);
    }
    return api.get('/schools/meta/cities');
  },
  stats: (params?: Record<string, string>) => api.get('/schools/meta/stats', { params }),
  academicians: (name: string) =>
    api.get(`/schools/${encodeURIComponent(name)}/academicians`),
};

// 高中管理（中考）
export const highSchoolsApi = {
  list: (params?: Record<string, string | number>) => api.get('/high-schools', { params }),
};

export interface MajorCompareSchool {
  school_name: string;
  is985: number;
  is211: number;
  city: string;
  school_province: string;
  trend: 'up' | 'down' | 'stable' | null;
  scores: Record<string, { lowest_score: number; lowest_rank: number; avg_score: number; batch_name: string }>;
}

export interface MajorCompareResult {
  major_name: string;
  schools: MajorCompareSchool[];
  years: string[];
  total_schools: number;
}

export interface HotMajor {
  major_name: string;
  school_count: number;
  record_count: number;
  latest_year: number;
}

// 专业
export const majorsApi = {
  list: (params?: Record<string, string | number>) => api.get('/majors', { params }),
  names: (search?: string) => api.get('/majors/names', { params: { search } }),
  bySchool: (name: string) => api.get(`/majors/${encodeURIComponent(name)}/schools`),
  compare: (name: string, year?: number, schoolType?: string) =>
    api.get('/majors/compare', { params: { name, year, schoolType } }),
  hot: (limit?: number) => api.get('/majors/hot', { params: { limit: limit || 12 } }),
};

// 推荐
export const recommendApi = {
  byStudent: (student_id: number, province_rank?: number) =>
    api.get('/recommendation/schools', { params: { student_id, province_rank } }),
  byRank: (grade_rank: number, student_id?: number) =>
    api.get('/recommendation/rank-convert', { params: { grade_rank, student_id: student_id || 1 } }),
  byScore: (score: number, year?: number) =>
    api.get('/recommendation/by-score', { params: { score, year: year || 2024 } }),
  majors: (params: { student_id?: number; interest?: string; province_rank?: number }) =>
    api.get('/recommendation/majors', { params }),
};

// 偏好
export const preferencesApi = {
  schema: (student_id: number) => api.get('/preferences/schema', { params: { student_id } }),
  get: (student_id: number) => api.get('/preferences', { params: { student_id } }),
  update: (key: string, value: unknown, student_id: number) =>
    api.put(`/preferences/${key}`, { student_id, value }),
  batch: (student_id: number, preferences: Record<string, unknown>) =>
    api.post('/preferences/batch', { student_id, preferences }),
  // 志愿单
  getVolunteer: (student_id: number) => api.get('/preferences/volunteer', { params: { student_id } }),
  addVolunteer: (data: { student_id: number; school_name: string; major_name?: string; category?: string; notes?: string }) =>
    api.post('/preferences/volunteer', data),
  updateVolunteer: (id: number, data: Record<string, unknown>) =>
    api.put(`/preferences/volunteer/${id}`, data),
  deleteVolunteer: (id: number) => api.delete(`/preferences/volunteer/${id}`),
};

// 一分一段
export const rankTableApi = {
  list: (year?: number) => api.get('/rank-table', { params: { year, subject_type: '物理类' } }),
  scoreToRank: (score: number, year?: number) =>
    api.get('/rank-table/score-to-rank', { params: { score, year: year || 2024, subject_type: '物理类' } }),
};

// 数据管理
export const dataAdminApi = {
  status: () => api.get('/data-admin/status'),
  gaps: () => api.get('/data-admin/gaps'),
  crawl: (type: string, year?: number[]) => api.post(`/data-admin/crawl/${type}`, { year }),
  taskStatus: (id: number) => api.get(`/data-admin/task/${id}`),
  importBuiltin: () => api.post('/data-admin/import-schools-builtin'),
  academicianStatus: () => api.get('/data-admin/academician-status'),
  crawlAcademicians: () => api.post('/data-admin/crawl/academicians'),
};

// ==================== 错题本模块 ====================

export interface WrongQuestion {
  id: number;
  student_id: number;
  exam_id?: number;
  subject: string;
  exam_name: string;
  question_content: string;
  question_images: string;
  correct_answer: string;
  student_answer: string;
  error_type: string;
  difficulty: string;
  attribution_level: string;
  knowledge_points: string;
  status: string;
  similar_questions: string;
  review_count: number;
  last_review_at?: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface KnowledgePoint {
  id: number;
  student_id: number;
  name: string;
  subject: string;
  description: string;
  mastery_level: number;
  wrong_count: number;
  review_count: number;
  last_reviewed_at?: string;
  created_at: string;
}

export interface SimilarQuestion {
  id: number;
  wrong_question_id: number;
  content: string;
  answer: string;
  source: string;
  created_at: string;
}

export interface Exam {
  id: number;
  exam_name: string;
  exam_date: string;
  exam_type: string;
  total_score: number;
}

// 错题管理
export const wrongQuestionsApi = {
  list: (params: {
    student_id?: number;
    subject?: string;
    error_type?: string;
    status?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }) => api.get('/wrong-questions', { params }),
  get: (id: number) => api.get(`/wrong-questions/${id}`),
  availableExams: (student_id?: number) => api.get('/wrong-questions/available-exams', { params: { student_id } }),
  create: (data: Partial<WrongQuestion>) => api.post('/wrong-questions', data),
  batchImport: (data: { student_id: number; subject: string; exam_name?: string; questions: any[] }) =>
    api.post('/wrong-questions/batch', data),
  update: (id: number, data: Partial<WrongQuestion>) => api.put(`/wrong-questions/${id}`, data),
  delete: (id: number) => api.delete(`/wrong-questions/${id}`),
  review: (id: number, status: string) => api.put(`/wrong-questions/${id}/review`, { status }),
  stats: (student_id?: number) => api.get('/wrong-questions/stats/overview', { params: { student_id } }),
  subjects: (student_id?: number) => api.get('/wrong-questions/subjects', { params: { student_id } }),
};

// 知识点管理
export const knowledgePointsApi = {
  list: (params: {
    student_id?: number;
    subject?: string;
    search?: string;
    sort_by?: string;
  }) => api.get('/knowledge-points', { params }),
  get: (id: number) => api.get(`/knowledge-points/${id}`),
  upsert: (data: Partial<KnowledgePoint>) => api.post('/knowledge-points/upsert', data),
  batchUpsert: (student_id: number, points: Array<{ name: string; subject: string; description?: string }>) =>
    api.post('/knowledge-points/batch-upsert', { student_id, points }),
  updateMastery: (id: number, mastery_level?: number, delta?: number) =>
    api.put(`/knowledge-points/${id}/mastery`, { mastery_level, delta }),
  delete: (id: number) => api.delete(`/knowledge-points/${id}`),
};

// AI 功能
export const aiApi = {
  extractKnowledge: (student_id: number, question_content: string, subject?: string) =>
    api.post('/ai/extract-knowledge', { student_id, question_content, subject }),
  generateSimilar: (student_id: number, question_content: string, correct_answer: string, subject?: string, knowledge_points?: string[]) =>
    api.post('/ai/generate-similar', { student_id, question_content, correct_answer, subject, knowledge_points }),
  analyze: (student_id: number, question_content: string, student_answer?: string, correct_answer?: string, subject?: string) =>
    api.post('/ai/analyze', { student_id, question_content, student_answer, correct_answer, subject }),
  configStatus: () => api.get('/ai/config-status'),
};

// LLM 配置
export interface LLMConfig {
  provider_name: string;
  base_url: string;
  api_key?: string;
  api_key_masked?: string;
  model: string;
  vision_model?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface ProviderPreset {
  name: string;
  label: string;
  baseUrl: string;
  defaultModel: string;
  models: string[];
  visionModels: string[];
}

export const llmConfigApi = {
  getPresets: () => api.get<{ data: ProviderPreset[] }>('/llm-config/presets'),
  get: () => api.get<{ data: LLMConfig | null }>('/llm-config'),
  save: (config: { provider_name: string; base_url: string; api_key: string; model: string; vision_model?: string; temperature?: number; max_tokens?: number }) =>
    api.put('/llm-config', config),
  validate: (config: { base_url: string; api_key: string; model: string }) =>
    api.post<{ success: boolean; valid: boolean; error?: string; latency?: number }>('/llm-config/validate', config),
  delete: () => api.delete('/llm-config'),
};

// 认证
export const authApi = {
  login: (username: string, password: string) =>
    api.post('/auth/login', { username, password }),
  register: (data: { username: string; password: string; displayName: string; studentInfo?: Record<string, unknown> }) =>
    api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  changePassword: (oldPassword: string, newPassword: string) =>
    api.put('/auth/change-password', { oldPassword, newPassword }),
  setSecurityQuestion: (question: string, answer: string) =>
    api.put('/auth/security-question', { question, answer }),
  forgotPassword: (username: string, securityAnswer: string, newPassword: string) =>
    api.post('/auth/forgot-password', { username, securityAnswer, newPassword }),
  checkUsername: (username: string) =>
    api.get(`/auth/check-username/${encodeURIComponent(username)}`),
  getSecurityQuestion: (username: string) =>
    api.get(`/auth/security-question/${encodeURIComponent(username)}`),
};

export default api;
